import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  listOooAdminSettings,
  syncOooSettings,
  updateOooAdminSettings,
  type EmployeeOooAdminRow,
} from "@/lib/employeeOoo";

interface OooAutomationPanelProps {
  onChanged?: () => void;
}

export function OooAutomationPanel({ onChanged }: OooAutomationPanelProps) {
  const [rows, setRows] = useState<EmployeeOooAdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [query, setQuery] = useState("");
  const [dirty, setDirty] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listOooAdminSettings();
      setRows(data);
      setDirty({});
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Einstellungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => {
      const blob = [row.user_display, row.user_login, row.email, row.substitute_display]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(needle);
    });
  }, [rows, query]);

  const enabledCount = useMemo(
    () => rows.filter((row) => row.automation_enabled).length,
    [rows],
  );

  const toggleRow = (login: string, enabled: boolean) => {
    setRows((current) =>
      current.map((row) =>
        row.user_login === login ? { ...row, automation_enabled: enabled } : row,
      ),
    );
    setDirty((current) => ({ ...current, [login]: enabled }));
  };

  const handleSave = async () => {
    const items = Object.entries(dirty).map(([user_login, automation_enabled]) => ({
      user_login,
      automation_enabled,
    }));
    if (items.length === 0) {
      toast.info("Keine Änderungen zum Speichern.");
      return;
    }
    setSaving(true);
    try {
      const saved = await updateOooAdminSettings(items);
      setRows(saved);
      setDirty({});
      toast.success("Abwesenheitsnotiz-Einstellungen gespeichert.");
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncOooSettings();
      await load();
      const errorCount = result.errors?.length ?? 0;
      if (errorCount > 0) {
        toast.error(`Sync abgeschlossen mit ${errorCount} Fehler(n).`);
      } else {
        toast.success(
          `Sync abgeschlossen: ${result.updated} aktualisiert, ${result.disabled} deaktiviert.`,
        );
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Sync fehlgeschlagen.");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="rounded-ios border border-separator bg-card p-5 shadow-ios">
        <h3 className="text-lg font-semibold text-label">Automatische Abwesenheitsnotiz</h3>
        <p className="mt-2 text-sm text-secondary">
          Aktivierte Mitarbeiter erhalten eine Outlook-Abwesenheitsnotiz basierend auf genehmigten
          Hero-Abwesenheiten. Wochenenden und Feiertage am Rand werden automatisch mit einbezogen.
          Vertretungen legen die Mitarbeiter in „Mein Bereich“ fest.
        </p>
        <p className="mt-1 text-sm text-secondary">
          Aktuell aktiviert: <strong>{enabledCount}</strong> von {rows.length} Mitarbeitern
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="block min-w-[240px] flex-1 space-y-1">
          <span className="eh-label">Suchen</span>
          <input
            type="search"
            className="eh-input w-full"
            placeholder="Name, E-Mail oder Login …"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <Button variant="secondary" onClick={() => void handleSync()} disabled={syncing}>
          {syncing ? "Synchronisiere …" : "Jetzt synchronisieren"}
        </Button>
        <Button onClick={() => void handleSave()} disabled={saving || Object.keys(dirty).length === 0}>
          {saving ? "Speichere …" : "Änderungen speichern"}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-ios border border-separator bg-card shadow-ios">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-separator bg-muted/40 text-left text-secondary">
              <th className="px-4 py-3 font-medium">Aktiv</th>
              <th className="px-4 py-3 font-medium">Mitarbeiter</th>
              <th className="px-4 py-3 font-medium">Vertretung</th>
              <th className="px-4 py-3 font-medium">Letzter Sync</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.user_login} className="border-b border-separator/60 last:border-0">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={row.automation_enabled}
                    onChange={(e) => toggleRow(row.user_login, e.target.checked)}
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-label">{row.user_display || row.user_login}</div>
                  <div className="text-xs text-secondary">{row.email || row.user_login}</div>
                </td>
                <td className="px-4 py-3 text-secondary">
                  {row.substitute_display || row.substitute_login || "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="text-secondary">
                    {row.last_sync_at
                      ? new Date(row.last_sync_at).toLocaleString("de-DE")
                      : "—"}
                  </div>
                  {row.last_sync_status === "error" && row.last_sync_error && (
                    <div className="mt-1 text-xs text-red-600">{row.last_sync_error}</div>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-secondary">
                  Keine Mitarbeiter gefunden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Alert variant="info" title="Microsoft 365">
        Für den automatischen Sync wird die Graph-Berechtigung{" "}
        <code className="text-xs">MailboxSettings.ReadWrite</code> (Application) benötigt. Der
        tägliche Lauf kann über einen Scheduled Call auf{" "}
        <code className="text-xs">POST /employee-times/ooo-settings/sync?token=…</code> eingerichtet
        werden.
      </Alert>
    </div>
  );
}
